import dayjs from "dayjs";

import { useMemo, useState } from "react";

import EditNoteIcon from "@mui/icons-material/EditNote";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Drawer, Paper, Typography } from "@mui/material";

import SearchBar from "@/components/SearchBar";
import { FlightWayEnum, flightWayOptions } from "@/constants";
import { AirportMapItem } from "@/constants/airport";
import useCheckSearchCondition from "@/hooks/useCheckSearchCondition";
import useEvent from "@/hooks/useEvent";
import { SearchConditionType } from "@/hooks/useSearchCondition";

const getLocationString = (listOfMaps: AirportMapItem[]) => {
  // Concatenate the list of strings with commas
  return listOfMaps.map((map) => map.iata).join(", ");
};

const getDateString = (startDate: number, endDate?: number) => {
  // Convert the start and end dates to YYYY-MM-DD string
  let ret = dayjs(startDate).format("MM/DD/YY");

  if (endDate) {
    ret += " - " + dayjs(endDate).format("MM/DD/YY");
  }

  return ret;
};

interface MobileSearchBarProps {
  isLoading?: boolean;
  data?: SearchConditionType;
  onSearch: (data: SearchConditionType) => void;
  onChange: (data: SearchConditionType) => void;
  cancelSearch?: () => void;
}

const MobileSearchBar = ({
  data,
  onSearch,
  onChange,
  isLoading,
  cancelSearch,
}: MobileSearchBarProps) => {
  const [openDrawer, setDrawer] = useState(false);
  const { checkSearchCondition } = useCheckSearchCondition();

  const mid =
    data?.flightWay === FlightWayEnum.OneWay ? " · " : <SwapHorizIcon sx={{ fontSize: "16px" }} />;

  const info = useMemo(() => {
    if (!data) return {};

    const { flightWay } = data;

    if (flightWay === FlightWayEnum.OneWay) {
      const name = flightWayOptions[0].name.toLocaleLowerCase();

      if (data.range) {
        return {
          startStr: getDateString(data.oneWayRangeStartDate, data.oneWayRangeEndDate),
          endStr: name,
        };
      } else {
        return {
          startStr: getDateString(data.oneWayDate),
          endStr: name,
        };
      }
    } else {
      if (data.range) {
        return {
          startStr: getDateString(data.departureRangeStartDate, data.departureRangeEndDate),
          endStr: getDateString(data.returnRangeStartDate, data.returnRangeEndDate),
        };
      } else {
        return {
          startStr: getDateString(data.roundTripDepartureDate),
          endStr: getDateString(data.roundTripReturnDate),
        };
      }
    }
  }, [data]);

  const handleSearch = useEvent((data) => {
    if (data) {
      const errMsg = checkSearchCondition(data);

      if (!errMsg) {
        onSearch && onSearch(data as SearchConditionType);
        setDrawer(false);
      }
    }
  });

  return (
    <div className="px-[10px]">
      <Paper
        elevation={4}
        className="mt-[12px] py-[10px] px-[12px] flex items-center !rounded-[16px]"
        onClick={() => setDrawer(true)}
      >
        <SearchIcon sx={{ color: (theme) => theme.palette.text.primary, fontSize: "22px" }} />

        <div className="flex flex-col flex-1 px-[8px] items-center">
          {data ? (
            <>
              <Typography color="textPrimary" variant="body2" className="flex items-center">
                {getLocationString(data.fromLocation)}
                {" - "}
                {getLocationString(data.toLocation)}
              </Typography>

              <Typography color="textPrimary" variant="body2" className="flex items-center">
                {info.startStr}
                {mid}
                {info.endStr}
              </Typography>
            </>
          ) : null}
        </div>

        <EditNoteIcon />
      </Paper>

      <Drawer
        keepMounted
        anchor="top"
        open={openDrawer}
        PaperProps={{ style: { borderRadius: "0 0 6px 6px" } }}
        onClose={() => setDrawer(false)}
      >
        <div className="relative h-full overflow-hidden px-[10px]">
          <div className="h-full">
            <SearchBar
              withMobileSearchBtn
              onChange={onChange}
              onSearch={handleSearch}
              isLoading={isLoading}
              cancelSearch={cancelSearch}
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default MobileSearchBar;
